import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store';
import '../styles/PaymentPage.css';

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAppStore();
  const isPaid = !!user?.is_paid;

  const membershipFeatures = [
    '内置 LLM 模型 API，无需自行配置即可使用 AI 解读',
    '平台托管 GitHub Token，公共接口不再频繁触发限流',
    '会员专属 AI 解读面板，可解锁全部分析功能',
    '热门项目 AI 解读优先排队生成，响应更快',
    '付费期内可随时联系管理员，获得人工支持'
  ];

  const handlePurchase = () => {
    if (!isAuthenticated) {
      alert('请先登录，再完成支付操作。');
      return;
    }
    if (isPaid) {
      alert('您已经是会员，无需重复购买。');
      return;
    }
    alert('支付通道尚未正式接入。当前为虚拟演示，如需开通，请联系管理员并提供账号信息，后台可手动为你开启 30 天体验会员。');
  };

  const plans = [
    {
      id: 'free',
      title: '免费版',
      price: '¥0',
      desc: '永久 · 基础功能',
      features: [
        'GitHub Trending & 项目搜索',
        '收藏夹与基础统计',
        '自带 Token / 模型才能使用 AI 解读',
        '公共接口偶尔会触发限流'
      ],
      highlight: false as const,
      actionText: '当前计划',
      disabled: true
    },
    {
      id: 'basic',
      title: '基础会员',
      price: '¥1',
      desc: '一次性体验 · 30 天',
      features: membershipFeatures,
      highlight: true as const,
      actionText: isPaid ? '已激活会员' : isAuthenticated ? '支付 ¥1 立即开通' : '登录后支付 ¥1',
      disabled: isPaid
    }
  ];

  return (
    <div className="payment-page-wrapper">
      <div className="payment-top-bar">
        <button className="payment-back-btn" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left" /> 返回上一页
        </button>
        {!isAuthenticated && (
          <span className="payment-tip text-muted">
            未登录也可以浏览会员权益，点击右上角登录后即可完成支付。
          </span>
        )}
      </div>

      <div className="payment-hero">
        <div className="payment-hero-content">
          <span className="payment-badge">限时 · 1 元体验</span>
          <h1>解锁会员，直接使用内置 AI 模型</h1>
          <p>
            会员无需配置 Token 或模型，就能使用平台托管的 LLM API 进行 AI 解读，公共接口也不会再遇到频繁限流问题。
          </p>
          <p className="payment-legal-tip text-muted">
            购买前请先阅读
            <a href="/legal/service" target="_blank" rel="noopener noreferrer">《服务声明》</a>、
            <a href="/legal/privacy" target="_blank" rel="noopener noreferrer">《隐私政策》</a>和
            <a href="/legal/refund" target="_blank" rel="noopener noreferrer">《退款政策》</a>。
          </p>
        </div>
      </div>

      <div className="container payment-plan-grid">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`plan-card ${plan.highlight ? 'plan-premium' : 'plan-free'} ${
              plan.highlight && isPaid ? 'plan-active' : ''
            }`}
          >
            <div className="plan-header">
              <div>
                <h4>{plan.title}</h4>
                <p>{plan.desc}</p>
              </div>
              <div className="plan-price">{plan.price}</div>
            </div>

            {plan.id === 'basic' && (
              <div className="plan-user-info">
                <div>
                  <span className="label">当前账号</span>
                  <strong>{isAuthenticated ? user?.username || '未知用户' : '未登录'}</strong>
                </div>
                <div>
                  <span className="label">会员状态</span>
                  <span className={`status-dot ${isPaid ? 'status-active' : 'status-inactive'}`}>
                    {isPaid ? '已开通' : '未开通'}
                  </span>
                </div>
              </div>
            )}

            <ul className={`plan-feature-list ${plan.highlight ? 'large' : ''}`}>
              {plan.features.map((item, index) => (
                <li key={index}>
                  {plan.highlight ? <i className="fas fa-check-circle" /> : <span className="dot" />}
                  {item}
                </li>
              ))}
            </ul>

            <button
              className="plan-action-btn"
              onClick={plan.highlight ? handlePurchase : undefined}
              disabled={plan.disabled}
            >
              {plan.actionText}
            </button>
          </div>
        ))}
      </div>

      <div className="container payment-note">
        <div className="alert alert-warning">
          <strong>虚拟支付说明：</strong>
          当前仅展示会员权益与费用提示，支付通道尚未正式接入。如需开通，请联系管理员并提供账号信息，后台可手动为你开启会员，具体退款规则详见
          <a href="/legal/refund" target="_blank" rel="noopener noreferrer">《退款政策》</a>。
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;

